import { images } from "./images";
import { projects } from "./data";

const services = [
  {
    id: "custom-interior",
    name: "Custom Interior",
    tagline: "Full-home and full-venue interiors, composed room by room.",
    bullets: [
      "Room-by-room concept and layouts",
      "Furniture, lighting and material planning",
      "Full supervision until handover",
    ],
    img: images.hero,
  },
  {
    id: "interior-design",
    name: "Interior Design",
    tagline: "Concept, layout and 3D design that give your project a clear direction.",
    bullets: [
      "Concept boards and mood direction",
      "3D visualisation of every key room",
      "Detailed technical drawings",
    ],
    img: images.editorial,
  },
  {
    id: "kitchen-set",
    name: "Custom Kitchen Set",
    tagline: "Kitchens built to measure — solid wood, plywood or lightweight concrete.",
    bullets: [
      "Solid wood, plywood or lightweight concrete",
      "Stone, solid-surface and timber worktops",
      "Ventilated, tropical-climate-ready construction",
    ],
    img: images.kitchenDark,
  },
  {
    id: "bedroom-set",
    name: "Bedroom Set",
    tagline: "Beds, wardrobes and side tables designed as one calm set.",
    bullets: [
      "Bed frames, headboards and side tables",
      "Built-in and walk-in wardrobes",
      "Soft, layered lighting design",
    ],
    img: images.bedroomWarm,
  },
  {
    id: "tv-backdrop",
    name: "TV Backdrop",
    tagline: "Feature walls that frame the living room and hide the cables.",
    bullets: [
      "Slatted wood, stone or plaster finishes",
      "Concealed cabling and media storage",
      "Ambient, concealed lighting",
    ],
    img: images.tvBackdrop,
  },
  {
    id: "wall-panel",
    name: "Wall Panel",
    tagline: "Slatted, fluted and solid panels that add warmth and rhythm.",
    bullets: [
      "Fluted, slatted and solid panels",
      "Wall-to-ceiling continuity",
      "Acoustic and texture options",
    ],
    img: images.texture,
  },
  {
    id: "storage",
    name: "Storage",
    tagline: "Made-to-measure closets, shelving and hidden storage.",
    bullets: [
      "Made-to-measure closets and shelving",
      "Hidden storage and joinery details",
      "Solutions for every corner",
    ],
    img: images.wardrobe,
  },
  {
    id: "custom-furniture",
    name: "Custom Furniture",
    tagline: "One-off tables, seating and cabinetry in teak and hardwood.",
    bullets: [
      "One-off tables, seating and cabinetry",
      "Solid teak and hardwood builds",
      "Finished by hand in our workshop",
    ],
    img: images.dining,
  },
  {
    id: "design-build",
    name: "Interior Design & Build",
    tagline: "One accountable team from design drawings to on-site installation.",
    bullets: [
      "One team from sketch to installation",
      "Permits, vendors and timeline managed",
      "Single point of accountability",
    ],
    img: images.villaArchitecture,
  },
];

export const en = {
  meta: {
    title: "Samasta Studio — Interior Design & Custom Furniture, Bali",
  },
  nav: {
    home: "Home",
    about: "About",
    services: "Services",
    portfolio: "Portfolio",
    contact: "Contact",
    cta: "Konsultasi",
    waMessage: "Hello Samasta Studio, I'd like to discuss an interior project.",
    menuLabel: "Menu",
    closeLabel: "Close",
  },
  common: {
    explorePortfolio: "Explore Portfolio",
    consultWhatsapp: "Consult on WhatsApp",
    viewAll: "View all projects",
    allServices: "All services",
    aboutStudio: "About the studio",
    discussProject: "Discuss on WhatsApp",
    chatWhatsapp: "Chat on WhatsApp",
    sendWhatsapp: "Send via WhatsApp",
    craftedInBali: "Crafted in Bali",
    rights: "All rights reserved.",
    included: "What's included",
  },
  home: {
    hero: {
      eyebrow: "Interior Design & Custom Furniture — Bali",
      line1: "Timeless interiors,",
      line2: "crafted in Bali.",
      sub: "Samasta Studio designs and builds bespoke interiors and custom furniture — from a single wall panel to a complete villa — for homes, villas, restaurants and shops across the island.",
      ctaPrimary: "Explore Portfolio",
      ctaSecondary: "Consult on WhatsApp",
      location: "Canggu · Seminyak · Uluwatu · Ubud",
      heroCaption: "Custom teak joinery — studio workshop",
      scroll: "Scroll to explore",
    },
    marquee: [
      "Custom Interiors",
      "Kitchen Sets",
      "Bedroom Sets",
      "TV Backdrops",
      "Wall Panels",
      "Storage",
      "Custom Furniture",
      "Design & Build",
    ],
    philosophy: {
      eyebrow: "Philosophy",
      title:
        "We craft spaces that honor materiality, light and the quiet warmth of tropical living.",
      p1: "Every Samasta project begins with listening — to the architecture, to the climate, and to the people who will live and work inside. Nothing is sketched before we understand how a space is actually used.",
      p2: "From a single wall panel to a complete design-and-build project, we combine Balinese artisanship with clean, modern precision — designed, fabricated and installed by one team.",
      link: "About the studio",
      caption: "Solid teak — studio workshop",
    },
    services: {
      eyebrow: "What we do",
      title: "From one wall to a whole home.",
      sub: "Focused disciplines, one studio — everything designed, built and installed in-house.",
      link: "All services",
      explore: "Explore",
    },
    portfolio: {
      eyebrow: "Selected work",
      title: "Spaces we have shaped.",
      link: "View all projects",
    },
    process: {
      eyebrow: "Process",
      title: "From first conversation to final installation.",
      steps: [
        {
          n: "01",
          t: "Consultation & Concept",
          d: "We listen, visit the site and shape a clear concept and budget direction.",
        },
        {
          n: "02",
          t: "3D Design & Materials",
          d: "You review 3D visuals, material samples and technical drawings before anything is built.",
        },
        {
          n: "03",
          t: "Workshop Fabrication",
          d: "Your furniture and joinery are built to measure in our Bali workshop.",
        },
        {
          n: "04",
          t: "Installation & Finishing",
          d: "Our own team installs, details and hands over a finished space.",
        },
      ],
    },
    cta: {
      title: "Ready to transform your space?",
      sub: "Tell us about your project — we usually reply within a few hours.",
      button: "Consult on WhatsApp",
      chipsLabel: "Or start with a topic",
      chips: ["Kitchen Set", "Full Interior", "TV Backdrop", "Custom Furniture"],
    },
  },
  about: {
    hero: {
      eyebrow: "About Samasta Studio",
      line1: "Design, wood and",
      line2: "quiet precision.",
      sub: "Samasta Studio is an interior design and custom furniture practice based in Bali — designing, building and installing everything with our own team.",
    },
    story: {
      eyebrow: "Our story",
      title: "Two traditions, one workshop.",
      p1: "Samasta Studio works at the intersection of two worlds: the island's deep tradition of woodworking, and a contemporary, minimalist design language. The result is interiors that feel calm and modern, yet unmistakably Balinese.",
      p2: "From our workshop, a small team of designers and carpenters takes each project from first conversation to final installation — designing, building and fitting everything ourselves, so nothing is lost in translation.",
      caption: "The workshop bench",
    },
    values: {
      eyebrow: "What we believe",
      title: "A quieter way to build.",
      items: [
        {
          n: "01",
          t: "Designed around you",
          d: "Every interior starts with how you live, cook, work and rest — not with a fixed style.",
        },
        {
          n: "02",
          t: "Made, not assembled",
          d: "Our furniture is fabricated in our own Bali workshop, by artisans we work with every day.",
        },
        {
          n: "03",
          t: "Honest process",
          d: "Clear drawings, clear materials, clear timelines — from concept to installation.",
        },
      ],
    },
    materials: {
      eyebrow: "Materials",
      title: "Honest materials, built for the tropics.",
      items: [
        {
          t: "Solid Teak & Hardwood",
          d: "Dense, durable and beautifully grained — the backbone of our furniture.",
          img: "woodShelves",
        },
        {
          t: "Slatted & Fluted Panels",
          d: "Rhythm and shadow for walls and ceilings, made to measure.",
          img: "texture",
        },
        {
          t: "Natural Stone & Terrazzo",
          d: "Cool underfoot and timeless — for kitchens, bathrooms and floors.",
          img: "editorial",
        },
        {
          t: "Rattan & Natural Fiber",
          d: "Woven texture that softens clean, modern lines.",
          img: "bedroomCanopy",
        },
      ],
    },
    cta: {
      title: "Let's talk about your project.",
      sub: "Tell us about your space — we'll take it from there.",
    },
  },
  servicesPage: {
    hero: {
      eyebrow: "Services",
      title: "Interior & furniture, made to order.",
      sub: "Nine focused disciplines, one accountable studio. Open a service to see what's included — then start the conversation on WhatsApp.",
    },
    discuss: "Discuss on WhatsApp",
    included: "What's included",
  },
  portfolioPage: {
    hero: {
      eyebrow: "Portfolio",
      title: "Work that lives in Bali.",
      sub: "A selection of interiors, kitchens and furniture — designed, built and installed by Samasta Studio.",
    },
  },
  contact: {
    hero: {
      eyebrow: "Contact",
      title: "Let's start with a conversation.",
      sub: "No forms, no waiting — message us directly on WhatsApp and tell us about your space.",
    },
    wa: {
      title: "Chat with us",
      sub: "The fastest way to reach the studio — we usually reply within a few hours.",
      button: "Chat on WhatsApp",
      numberLabel: "WhatsApp",
    },
    studio: {
      title: "Studio & workshop",
      locationLabel: "Location",
      location: "Canggu, Bali, Indonesia",
      emailLabel: "Email",
      email: "hello@samastastudio.id",
      hoursLabel: "Hours",
      hours: "Monday – Saturday · 09.00 – 17.00 WITA",
    },
    composer: {
      title: "Compose your message",
      sub: "Pick a topic, write a line — WhatsApp opens with everything ready to send.",
      serviceLabel: "I'm interested in",
      messageLabel: "Your message",
      placeholder: "Tell us about your space, location and timeline…",
      button: "Send via WhatsApp",
      intro: "Hello Samasta Studio,",
    },
    faq: {
      eyebrow: "FAQ",
      title: "Good to know.",
      items: [
        {
          q: "Do you work outside Bali?",
          a: "Custom pieces can be shipped to other islands in Indonesia — message us with your location and we'll discuss the logistics.",
        },
        {
          q: "Can I order furniture without a full interior project?",
          a: "Yes. Custom furniture, kitchen sets and wall panels can be ordered individually, made to measure in our workshop.",
        },
        {
          q: "How does a project start?",
          a: "With a WhatsApp conversation and, when possible, a site visit. From there we prepare a concept, a budget direction and a timeline.",
        },
        {
          q: "Where is the furniture made?",
          a: "In our own workshop in Bali — in solid teak, hardwood and premium plywood, finished by hand.",
        },
        {
          q: "Do you provide design only?",
          a: "Yes — design-only engagements are possible, with detailed drawings ready for your own builder.",
        },
      ],
    },
  },
  footer: {
    blurb:
      "Interior design & custom furniture studio based in Bali — custom interiors, kitchen sets, bedroom sets, wall panels, storage and bespoke furniture.",
    menuTitle: "Menu",
    servicesTitle: "Services",
    contactTitle: "Contact",
    location: "Bali, Indonesia",
  },
  categories: [
    { id: "all", label: "All Projects" },
    { id: "villas", label: "Villas & Living" },
    { id: "kitchen", label: "Kitchen Sets" },
    { id: "bedroom", label: "Bedroom Suites" },
    { id: "panels", label: "Backdrops & Panels" },
    { id: "commercial", label: "Commercial" },
  ],
  services,
  projects,
};
